import type { ReactNode } from 'react'
import { Send } from 'lucide-react'
import {
  Badge,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@teach/ui'
import { ExperimentalNote } from '../shared/ExperimentalNote'
import { ACCESS_META } from '@/features/instructions/lib/accessMeta'
import type { AccessType } from '@/features/instructions/types'
import { ACCESS_ORDER, isAccessRaise } from './accessLevels'
import { useMessages } from '@/i18n'
import { messages } from './messages'

// Список — функцией, а не константой модуля: `label` у `ACCESS_META` — геттер на
// языке интерфейса, и разворот `...` при загрузке модуля замораживал его русским
// («В каталоге» при английском интерфейсе).
const accessOpts = () =>
  ACCESS_ORDER.map((id) => ({ id, icon: ACCESS_META[id].icon, label: ACCESS_META[id].label }))

interface Props {
  access: AccessType
  onAccessChange: (next: AccessType) => void
  /** Гасит селект целиком: нет прав, идёт запрос, курс на проверке. */
  disabled: boolean
  /** Запрещает РОВНО повышение открытости (см. `AccessSection`). */
  raiseBlocked: boolean
  /** `canPublishToCatalog` из состояния модерации. */
  catalogAllowed: boolean
  /** Отсчёт `429`, «4:12», или `null`. */
  retryLabel: string | null
  /** Подпись над селектом: «Доступ к курсу» / «Доступ к статье». */
  label: string
  /** Заглушка «Свой TMA — Скоро» (у курса, как в рефе). */
  showTmaStub: boolean
  /** Ширина триггера (у статьи — половина колонки, как до переноса). */
  triggerClassName?: string
  /** Плашка про экспериментальный доступ — правее селекта, а не под ним. */
  noteBeside?: boolean
  /** Почему закрытый курс нельзя опубликовать («не заполнены название и описание»).
   *  Гасит селект с подсказкой при наведении; уже опубликованный курс закрыть можно всегда. */
  unfilledHint?: string
}

// Погашенный селект не ловит наведение, поэтому подсказку вешаем на обёртку.
const HintWrap = ({ hint, children }: { hint?: string; children: ReactNode }) =>
  hint ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="block cursor-not-allowed">
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  ) : (
    <>{children}</>
  )

export const AccessSelect = ({
  access,
  onAccessChange,
  disabled,
  raiseBlocked,
  catalogAllowed,
  retryLabel,
  label,
  showTmaStub,
  triggerClassName,
  noteBeside = false,
  unfilledHint,
}: Props) => {
  const m = useMessages(messages)
  const opts = accessOpts()
  const cur = opts.find((o) => o.id === access) ?? opts[0]
  const CurIcon = cur.icon
  // Курс, уже лежащий в каталоге, остаётся выбранным «В каталоге» и без права:
  // строку гасим только как ЦЕЛЬ перехода, иначе селект врал бы о текущем доступе.
  const isLocked = (id: AccessType) =>
    (raiseBlocked && isAccessRaise(access, id)) ||
    (id === 'public' && !catalogAllowed && access !== 'public')
  // Выбирать нечего (закрытый курс под гейтом) — гасим сам селект, чтобы он не
  // открывался списком из одних недоступных строк.
  const nothingToPick = ACCESS_ORDER.every((id) => id === access || isLocked(id))
  // Публиковать нечего: у закрытого курса нет названия или описания.
  const unfilled = Boolean(unfilledHint) && access === 'private'

  // Под селектом, а не в плашке проверки: `429` приходит и без отклонённой заявки.
  const retryLine = retryLabel ? (
    <p className="text-xs text-muted-foreground">{m.retryIn(retryLabel)}</p>
  ) : null

  // Экспериментальный доступ: каталог закрыт, доступна только публикация по ссылке.
  const experimentalNote = !catalogAllowed ? (
    <ExperimentalNote title={m.experimentalTitle} body={m.experimentalBody} />
  ) : null

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <div className={noteBeside ? 'grid items-start gap-4 md:grid-cols-2' : 'space-y-2'}>
        <div className="space-y-2">
          <HintWrap hint={unfilled ? unfilledHint : undefined}>
            <Select
              value={access}
              onValueChange={(v) => onAccessChange(v as AccessType)}
              disabled={disabled || nothingToPick || unfilled}
            >
              <SelectTrigger className={triggerClassName}>
                <div className="flex items-center gap-2.5 font-medium">
                  <CurIcon className="w-4 h-4 shrink-0" /> {cur.label}
                </div>
              </SelectTrigger>
              <SelectContent>
                {opts.map(({ id, label, icon: Icon }) => (
                  <SelectItem key={id} value={id} disabled={isLocked(id)}>
                    <span className="flex items-center gap-2">
                      <Icon className="w-4 h-4" /> {label}
                    </span>
                  </SelectItem>
                ))}
                {/* Свой Telegram Mini App — заглушка «Скоро», как в рефе */}
                {showTmaStub ? (
                  <SelectItem value="tma" disabled>
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" /> {m.ownTma}
                      <Badge variant="neutral" className="ml-1">
                        {m.soon}
                      </Badge>
                    </span>
                  </SelectItem>
                ) : null}
              </SelectContent>
            </Select>
          </HintWrap>
          {retryLine}
        </div>
        {experimentalNote}
      </div>
    </div>
  )
}
